/**
 * NotificationBell Component
 *
 * Bell icon with badge showing unread alert count.
 * Opens a dropdown panel with list of alerts.
 *
 * @module components/common/NotificationBell
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAlerts } from '../../hooks/useAlerts';
import { cn } from '../../utils/cn';

/**
 * NotificationBell component
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    alerts,
    unreadCount,
    hasUnread,
    markAsRead,
    markAllAsRead,
  } = useAlerts();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle alert click
  const handleAlertClick = async (alertId: string) => {
    await markAsRead(alertId);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Format timestamp
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('alerts.justNow', 'À l\'instant');
    if (diffMins < 60) return t('alerts.minutesAgo', '{{count}} min', { count: diffMins });
    if (diffHours < 24) return t('alerts.hoursAgo', '{{count}} h', { count: diffHours });
    if (diffDays < 7) return t('alerts.daysAgo', '{{count}} j', { count: diffDays });
    return date.toLocaleDateString();
  };

  return (
    <div className="relative z-20" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative p-2 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center',
          hasUnread
            ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30'
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
        )}
        title={t('alerts.notifications', 'Notifications')}
      >
        <BellIcon className="w-5 h-5" />
        
        {/* Badge */}
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full z-10">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">
              {t('alerts.title', 'Alertes')}
            </h3>
            {hasUnread && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
              >
                {t('alerts.markAllRead', 'Tout marquer comme lu')}
              </button>
            )}
          </div>

          {/* Alert list */}
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                <BellOffIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{t('alerts.noAlerts', 'Aucune alerte')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {alerts.map((alert) => (
                  <li key={alert.id}>
                    <button
                      onClick={() => handleAlertClick(alert.id)}
                      className={cn(
                        'w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors',
                        !alert.isRead && 'bg-violet-50/50 dark:bg-violet-900/20'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        {/* Unread indicator */}
                        <div className="flex-shrink-0 mt-1.5">
                          {!alert.isRead ? (
                            <div className="w-2 h-2 bg-violet-500 rounded-full" />
                          ) : (
                            <div className="w-2 h-2" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'text-sm',
                            alert.isRead
                              ? 'text-slate-600 dark:text-slate-400'
                              : 'text-slate-900 dark:text-slate-100 font-medium'
                          )}>
                            {alert.message}
                          </p>
                          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            {formatTime(alert.triggeredAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700">
            <Link
              to="/settings?tab=alerts"
              onClick={() => setIsOpen(false)}
              className="block text-center text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
            >
              {t('alerts.manageAlerts', 'Gérer les alertes')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Bell icon
const BellIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

// Bell off icon (for empty state)
const BellOffIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

export default NotificationBell;
